import { DocsCallout, DocsContent, DocsLayout, H2, H3 } from '@/components/docs'
import { Card } from '@/components/ui/card'

export default function DocsNaturalLanguage() {
  return (
    <DocsLayout>
      <DocsContent
        title="Natural Language Queries"
        description="Ask questions about your investigation data in plain English. XENDREL translates your questions into graph queries and returns relevant results."
        previousPage={{ title: 'Timeline View', href: '/docs/timeline-view' }}
        nextPage={{ title: 'Best Practices', href: '/docs/best-practices' }}
      >
        <H2 id="how-it-works">How It Works</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          XENDREL's natural language interface lets you query your knowledge graph using everyday
          language instead of complex query syntax. Simply type a question, and the AI translates it
          into a graph query, executes it, and presents the results.
        </p>

        <DocsCallout variant="info" title="Opening the Chat">
          Click the <strong className="text-foreground">Chat</strong> button in the case toolbar to
          open the query interface. You can type questions directly into the input field.
        </DocsCallout>

        <H2 id="types-of-questions">Types of Questions You Can Ask</H2>

        <H3 id="entity-discovery">Entity Discovery</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Find entities of a specific type or with certain properties:
        </p>

        <div className="space-y-2 my-4">
          <QueryExample query="Show me all people in this case" />
          <QueryExample query="List all organizations" />
          <QueryExample query="Find all phone numbers" />
          <QueryExample query="What locations are mentioned?" />
        </div>

        <H3 id="relationship-queries">Relationship Queries</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Explore connections between entities:
        </p>

        <div className="space-y-2 my-4">
          <QueryExample query="Who is connected to John Smith?" />
          <QueryExample query="What organizations is Sarah Johnson associated with?" />
          <QueryExample query="Show me all relationships between Acme Corp and individuals" />
          <QueryExample query="Who are the directors of Global Holdings?" />
        </div>

        <H3 id="property-searches">Property Searches</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Find entities with specific attributes:
        </p>

        <div className="space-y-2 my-4">
          <QueryExample query="Find all people born after 1980" />
          <QueryExample query="Show transactions over $100,000" />
          <QueryExample query="List addresses in New York" />
          <QueryExample query="Find emails containing @example.com" />
        </div>

        <H3 id="path-and-connection-queries">Path and Connection Queries</H3>
        <p className="text-foreground mb-4 leading-relaxed">Discover how entities are connected:</p>

        <div className="space-y-2 my-4">
          <QueryExample query="How is John Smith connected to ABC Corporation?" />
          <QueryExample query="What links Person A to Person B?" />
          <QueryExample query="Show the path between these two organizations" />
        </div>

        <H3 id="aggregation-queries">Aggregation Queries</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Get summary statistics about your data:
        </p>

        <div className="space-y-2 my-4">
          <QueryExample query="How many people are in this case?" />
          <QueryExample query="Count all organizations by type" />
          <QueryExample query="What's the total value of transactions?" />
        </div>

        <H2 id="understanding-results">Understanding Results</H2>

        <H3 id="result-types">Result Types</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Depending on your question, you'll receive different types of results:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Entity list</strong> - A list of matching entities
              that you can click to view in the graph
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Relationship paths</strong> - Chains of
              connections showing how entities are linked
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Counts and statistics</strong> - Numerical
              summaries of your data
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Text explanations</strong> - Narrative
              descriptions when appropriate
            </span>
          </li>
        </ul>

        <H3 id="interacting-with-results">Interacting with Results</H3>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Click on any entity in the results to select it in the graph</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Use "Show in graph" to focus the view on result entities</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Ask follow-up questions to drill deeper into results</span>
          </li>
        </ul>

        <H2 id="tips-for-better-queries">Tips for Better Queries</H2>

        <H3 id="be-specific">Be Specific</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          More specific questions get more focused results:
        </p>

        <div className="space-y-3 my-4">
          <QueryComparison
            poor="Show me people"
            better="Show me people who are directors of companies"
          />
          <QueryComparison
            poor="Find connections"
            better="Find connections between John Smith and financial institutions"
          />
        </div>

        <H3 id="use-entity-names">Use Entity Names</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          When asking about specific entities, use their exact names as they appear in your
          documents:
        </p>

        <div className="space-y-3 my-4">
          <QueryComparison
            poor="Who does John work with?"
            better="Who is John Michael Smith connected to?"
          />
        </div>

        <H3 id="ask-follow-ups">Ask Follow-up Questions</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          The chat maintains context, so you can ask follow-up questions:
        </p>

        <Card size="compact" className="rounded-lg p-4 bg-card/50 my-4">
          <div className="space-y-2 text-sm">
            <p className="text-muted-foreground">You: "Who are the directors of Acme Corp?"</p>
            <p className="text-muted-foreground">
              XENDREL: <em>Lists John Smith and Sarah Johnson</em>
            </p>
            <p className="text-muted-foreground">
              You: "What other organizations is John Smith associated with?"
            </p>
            <p className="text-muted-foreground">
              XENDREL: <em>Shows other organizations</em>
            </p>
          </div>
        </Card>

        <H3 id="specify-time-ranges">Specify Time Ranges</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          When your data includes dates, be explicit about time periods:
        </p>

        <div className="space-y-3 my-4">
          <QueryComparison
            poor="Show recent transactions"
            better="Show transactions from January 2024"
          />
        </div>

        <H2 id="example-queries">Example Queries by Use Case</H2>

        <H3 id="financial-investigation">Financial Investigation</H3>
        <div className="space-y-2 my-4">
          <QueryExample query="Show all transactions over $50,000" />
          <QueryExample query="Find accounts linked to suspicious entities" />
          <QueryExample query="What is the flow of money from Account A to Account B?" />
          <QueryExample query="List all wire transfers in the last 6 months" />
        </div>

        <H3 id="identity-verification">Identity Verification</H3>
        <div className="space-y-2 my-4">
          <QueryExample query="Find all documents for John Smith" />
          <QueryExample query="What passports are associated with this person?" />
          <QueryExample query="Show addresses used by this identity" />
          <QueryExample query="Are there duplicate identities with similar names?" />
        </div>

        <H3 id="network-analysis">Network Analysis</H3>
        <div className="space-y-2 my-4">
          <QueryExample query="Who are the most connected individuals?" />
          <QueryExample query="Find people with connections to multiple organizations" />
          <QueryExample query="Show the network around this entity" />
          <QueryExample query="What organizations have overlapping directors?" />
        </div>

        <H2 id="when-queries-dont-work">When Queries Don't Work</H2>

        <DocsCallout variant="tip" title="Troubleshooting">
          If your query doesn't return expected results:
          <ul className="mt-2 space-y-1">
            <li className="flex items-start gap-2">
              <span className="text-green-600 dark:text-green-400">•</span>
              <span>Try rephrasing the question with different words</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600 dark:text-green-400">•</span>
              <span>Check that the entity names match what's in your documents</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600 dark:text-green-400">•</span>
              <span>Start with a broader query and narrow down</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600 dark:text-green-400">•</span>
              <span>Use the structured view to verify the data exists</span>
            </li>
          </ul>
        </DocsCallout>

        <H3 id="common-issues">Common Issues</H3>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">No results</strong> - The entities might not exist
              or might be named differently in your data
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Too many results</strong> - Add more specific
              criteria to narrow down
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Wrong interpretation</strong> - Rephrase the
              question to be more explicit
            </span>
          </li>
        </ul>
      </DocsContent>
    </DocsLayout>
  )
}

function QueryExample({ query }: { query: string }) {
  return (
    <div className="px-4 py-2 rounded-lg bg-card border border-border text-foreground text-sm font-mono">
      "{query}"
    </div>
  )
}

function QueryComparison({ poor, better }: { poor: string; better: string }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-xs text-red-600 dark:text-red-400 w-12">Poor:</span>
        <span className="text-muted-foreground text-sm">"{poor}"</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-green-600 dark:text-green-400 w-12">Better:</span>
        <span className="text-foreground text-sm">"{better}"</span>
      </div>
    </div>
  )
}
